import { useEffect, useState, useMemo } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

import { Button } from '@@/buttons';
import { Modal } from '@@/modals';
import { ModalBody } from '@@/modals/Modal/ModalBody';
import { ModalHeader } from '@@/modals/Modal/ModalHeader';
import { ModalFooter } from '@@/modals/Modal/ModalFooter';
import { NavTabs } from '@@/NavTabs';
import { CodeEditor } from '@@/CodeEditor';
import { Alert } from '@@/Alert';
import { InlineLoader } from '@@/InlineLoader';
import { PortainerSelect } from '@@/form-components/PortainerSelect';

import { StackConversionResponse } from '../../../../../common/stacks/queries/useStackConversion';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  conversionData?: StackConversionResponse;
  isLoading?: boolean;
  error?: Error | null;
}

export function StackConversionPreviewModal({
  isOpen,
  onClose,
  conversionData,
  isLoading,
  error,
}: Props) {
  const [selectedFile, setSelectedFile] = useState<string | null>(null);

  const fileNames = useMemo(() => {
    if (!conversionData) return [];
    const names = Object.keys(conversionData.files);
    return names.sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
    );
  }, [conversionData]);

  const hasPVC = useMemo(() => {
    if (!conversionData) return false;
    return Object.values(conversionData.files).some((content) =>
      content.includes('kind: PersistentVolumeClaim')
    );
  }, [conversionData]);
  const defaultFile = fileNames[0] || null;

  const fileOptions = useMemo(
    () =>
      fileNames.map((fileName) => ({
        value: fileName,
        label: fileName,
      })),
    [fileNames]
  );

  const tabOptions = useMemo(
    () =>
      fileNames.map((fileName) => ({
        id: fileName,
        label: fileName,
        children: (
          <div className="max-h-[70vh] overflow-auto">
            <CodeEditor
              id={`conversion-preview-${fileName}`}
              value={conversionData?.files[fileName] || ''}
              type="yaml"
              readonly
              data-cy={`conversion-preview-file-${fileName}`}
              height="60vh"
            />
          </div>
        ),
      })),
    [fileNames, conversionData]
  );

  // Use dropdown for many files (>5), tabs for fewer files
  const useDropdown = fileNames.length > 5;
  const currentFile = selectedFile ?? defaultFile ?? null;

  // Calculate current file index for navigation
  const currentFileIndex = useMemo(() => {
    if (!currentFile) return -1;
    return fileNames.indexOf(currentFile);
  }, [currentFile, fileNames]);

  const canGoPrevious = currentFileIndex > 0;
  const canGoNext =
    currentFileIndex >= 0 && currentFileIndex < fileNames.length - 1;

  function handlePrevious() {
    if (canGoPrevious && currentFileIndex > 0) {
      setSelectedFile(fileNames[currentFileIndex - 1]);
    }
  }

  function handleNext() {
    if (canGoNext && currentFileIndex < fileNames.length - 1) {
      setSelectedFile(fileNames[currentFileIndex + 1]);
    }
  }

  function renderContent() {
    if (isLoading) {
      return (
        <InlineLoader>Converting stack to Kubernetes manifests...</InlineLoader>
      );
    }

    if (error) {
      return (
        <Alert color="error" title="Conversion Error">
          {error.message || 'Failed to convert stack'}
        </Alert>
      );
    }

    if (!conversionData) {
      return null;
    }

    return (
      <>
        {conversionData.helmNotSupported && (
          <Alert color="error" className="mb-4">
            Helm chart conversion is not supported by Kompose. Kubernetes
            manifests were generated instead.
          </Alert>
        )}
        {hasPVC && (
          <Alert color="warn" className="mb-4">
            This conversion includes PersistentVolumeClaims (PVCs). The
            generated PVCs will be empty because Docker volume data is not
            migrated. Ensure all volume data is backed up before proceeding.
          </Alert>
        )}
        {renderFileContent()}
      </>
    );
  }

  function renderFileContent() {
    if (!conversionData) {
      return null;
    }

    if (fileNames.length === 0) {
      return (
        <Alert color="info" title="No Files Generated">
          No manifest files were generated from the conversion.
        </Alert>
      );
    }

    if (fileNames.length === 1) {
      return (
        <div className="max-h-[70vh] overflow-auto">
          <CodeEditor
            id="conversion-preview-single"
            value={conversionData.files[fileNames[0]]}
            type="yaml"
            readonly
            data-cy="conversion-preview-single-file"
            height="60vh"
          />
        </div>
      );
    }

    return (
      <>
        {useDropdown ? (
          <>
            <div className="mb-4">
              <span className="control-label mb-2 block text-sm">
                Select manifest file:
              </span>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <PortainerSelect
                    value={currentFile ?? undefined}
                    onChange={(value) => setSelectedFile(value ?? null)}
                    options={fileOptions}
                    placeholder="Select a manifest file"
                    data-cy="conversion-preview-file-selector"
                    aria-label="Select manifest file"
                  />
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <span className="text-muted mr-1 text-xs">
                    {currentFileIndex >= 0
                      ? `${currentFileIndex + 1} / ${fileNames.length}`
                      : ''}
                  </span>
                  <Button
                    onClick={handlePrevious}
                    icon={ChevronUp}
                    color="none"
                    disabled={!canGoPrevious}
                    size="small"
                    data-cy="conversion-preview-previous-button"
                    aria-label="Previous file"
                  />
                  <Button
                    onClick={handleNext}
                    icon={ChevronDown}
                    color="none"
                    disabled={!canGoNext}
                    size="small"
                    data-cy="conversion-preview-next-button"
                    aria-label="Next file"
                  />
                </div>
              </div>
            </div>
            {currentFile && (
              <div className="max-h-[70vh] overflow-auto">
                <CodeEditor
                  id={`conversion-preview-${currentFile}`}
                  value={conversionData.files[currentFile] || ''}
                  type="yaml"
                  readonly
                  data-cy={`conversion-preview-file-${currentFile}`}
                  height="60vh"
                />
              </div>
            )}
          </>
        ) : (
          <div className="mb-2 flex items-center gap-2">
            <div className="flex-1 overflow-x-auto">
              <NavTabs<string>
                onSelect={setSelectedFile}
                selectedId={currentFile ?? undefined}
                options={tabOptions}
              />
            </div>
            <div className="mt-1 flex shrink-0 items-center gap-1 self-start">
              <span className="text-muted mr-1 text-xs">
                {currentFileIndex >= 0
                  ? `${currentFileIndex + 1} / ${fileNames.length}`
                  : ''}
              </span>
              <Button
                onClick={handlePrevious}
                icon={ChevronUp}
                color="none"
                disabled={!canGoPrevious}
                size="small"
                data-cy="conversion-preview-previous-button"
                aria-label="Previous file"
              />
              <Button
                onClick={handleNext}
                icon={ChevronDown}
                color="none"
                disabled={!canGoNext}
                size="small"
                data-cy="conversion-preview-next-button"
                aria-label="Next file"
              />
            </div>
          </div>
        )}
      </>
    );
  }

  // Set default selected file when data loads
  useEffect(() => {
    if (conversionData && !selectedFile && defaultFile) {
      setSelectedFile(defaultFile);
    }
  }, [conversionData, selectedFile, defaultFile]);

  // Reset selected file when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedFile(null);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <Modal onDismiss={onClose} size="xl" aria-label="Stack Conversion Preview">
      <ModalHeader title="Stack Conversion Preview" />
      <ModalBody>{renderContent()}</ModalBody>
      <ModalFooter>
        <Button onClick={onClose} data-cy="conversion-preview-close-button">
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
}
